const rk = (t) => (t?.rank ? `#${t.rank}` : '—');
const n1 = (v) => (v == null ? '—' : Number(v).toFixed(1).replace(/\.0$/, ''));
const FBS = new Set(['ACC', 'Big 12', 'Big Ten', 'SEC', 'Pac-12', 'C-USA', 'MAC', 'Mountain West', 'Independent', 'Sun Belt', 'American']);
const isSmall = (t) => !FBS.has(t?.conf);
const sg = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${n1(v)}`);
const better = (a, b, lower) => (a == null || b == null || a === b ? 0 : (lower ? a < b : a > b) ? 1 : -1);
const wpct = (r) => { const [w, l] = (r || '0-0').split('-').map(Number); return w / Math.max(1, w + l); };

// Plain-English reasons the pick wins (and any risks), from both teams' season numbers.
export const whyTheyWin = (g) => {
  const p = g.pick;
  const me = g.teams.find((t) => t.abbr === p.abbr);
  const op = g.teams.find((t) => t.abbr !== p.abbr);
  const f = me?.form || {};
  const o = op?.form || {};
  const out = [];
  if (!me || !op) return out;
  const mPct = wpct(me.record);
  const oPct = wpct(op.record);
  if (mPct > oPct) out.push({ ok: true, t: `Better record: ${me.record} vs ${op.record}` });
  else if (mPct < oPct) out.push({ ok: false, t: `Worse record (${me.record} vs ${op.record}) — the odds still favor them` });
  if (me?.rank && !op?.rank) out.push({ ok: true, t: `Ranked #${me.rank} vs an unranked team` });
  if (me.rank && op.rank && me.rank < op.rank) out.push({ ok: true, t: `Ranked higher: #${me.rank} vs #${op.rank}` });
  if (op?.rank && (!me?.rank || op.rank < me.rank)) out.push({ ok: false, t: `Opponent is ranked #${op.rank}` });
  if (isSmall(op) && !isSmall(me)) out.push({ ok: true, t: `${op.abbr} is a smaller-division (FCS) school — its stats below come against weaker teams, so the scoring numbers don't compare evenly` });
  if (f.ppg != null && o.papg != null) {
    const edge = f.ppg - o.papg;
    out.push({ ok: edge >= 0, t: `Offense scores ${n1(f.ppg)}/game; ${op.abbr} defense allows ${n1(o.papg)}/game` });
  }
  if (f.papg != null && o.ppg != null) {
    out.push({ ok: f.papg <= o.ppg, t: `Defense allows ${n1(f.papg)}/game; ${op.abbr} offense scores ${n1(o.ppg)}/game` });
  }
  if (f.margin != null && o.margin != null) out.push({ ok: f.margin > o.margin, t: `Season point margin ${sg(f.margin)} vs ${sg(o.margin)}` });
  if (me?.home_away === 'home') out.push({ ok: true, t: `Home game${me.home_record ? ` (${me.home_record} at home)` : ''}` });
  else out.push({ ok: false, t: `Road game${me?.away_record ? ` (${me.away_record} on the road)` : ''}` });
  if (p.lookahead) out.push({ ok: false, t: p.lookahead });
  const src = [['sportsbook', p.market_prob], ['ESPN', p.fpi_prob], ['team form', p.form_prob]].filter(([, v]) => v != null)
    .map(([k, v]) => `${k} ${Math.min(99, Math.round(v * 100))}%`).join(' · ');
  if (src) out.push({ ok: true, t: `Win chance sources: ${src}` });
  return out;
};

// Projected final: market total + favorite margin; falls back to season scoring vs allowed.
export const projectedScore = (g) => {
  const p = g.pick;
  const me = g.teams.find((t) => t.abbr === p.abbr);
  const op = g.teams.find((t) => t.abbr !== p.abbr);
  if (!me || !op) return null;
  const ou = parseFloat(g.over_under);
  let a; let b;
  if (ou && p.margin != null) { a = (ou + p.margin) / 2; b = (ou - p.margin) / 2; }
  else if (me.form?.ppg != null && op.form?.papg != null) { a = (me.form.ppg + op.form.papg) / 2; b = (op.form.ppg + me.form.papg) / 2; }
  else return null;
  return { me: me.abbr, op: op.abbr, a: Math.round(a), b: Math.max(0, Math.round(b)) };
};

// One-line summary: strongest edges + the biggest risk.
export const briefLine = (g) => {
  const me = g.teams.find((t) => t.abbr === g.pick.abbr);
  const op = g.teams.find((t) => t.abbr !== g.pick.abbr);
  if (!me || !op) return '';
  const f = me.form || {};
  const o = op.form || {};
  const bits = [];
  if (isSmall(op) && !isSmall(me)) bits.push('vs FCS school');
  if (f.ppg != null && o.papg != null && f.ppg > o.papg) bits.push(`scores ${n1(f.ppg)} vs D allowing ${n1(o.papg)}`);
  if (f.margin != null && o.margin != null && f.margin > o.margin) bits.push(`margin ${sg(f.margin)}/${sg(o.margin)}`);
  bits.unshift(me.home_away === 'home' ? 'home' : 'road');
  const risk = g.pick.lookahead ? 'big game next week' : op.rank && (!me.rank || op.rank < me.rank) ? `opponent ranked #${op.rank}` : wpct(me.record) < wpct(op.record) ? 'worse record' : null;
  return bits.slice(0, 3).join(' · ') + (risk ? ` · ⚠ ${risk}` : '');
};

const Cell = ({ children, hi }) => <td className={`py-0.5 px-1 text-center font-mono ${hi ? 'text-emerald-300 font-bold' : 'text-zinc-400'}`}>{children}</td>;

export const CfbMatchup = ({ g }) => {
  const p = g.pick;
  const me = g.teams.find((t) => t.abbr === p.abbr);
  const op = g.teams.find((t) => t.abbr !== p.abbr);
  const f = me?.form || {};
  const o = op?.form || {};
  const rows = [
    ['Record', me?.record, op?.record, better(wpct(me?.record), wpct(op?.record))],
    ['Rank', rk(me), rk(op), better(me?.rank || 999, op?.rank || 999, true)],
    ['Site', me?.home_away === 'home' ? `Home ${me?.home_record || ''}` : `Road ${me?.away_record || ''}`, op?.home_away === 'home' ? `Home ${op?.home_record || ''}` : `Road ${op?.away_record || ''}`],
    ['Scores/game', n1(f.ppg), n1(o.ppg), better(f.ppg, o.ppg)],
    ['Allows/game', n1(f.papg), n1(o.papg), better(f.papg, o.papg, true)],
    ['Point margin', sg(f.margin), sg(o.margin), better(f.margin, o.margin)],
  ];
  return (
    <div className="px-2 pb-2" data-testid={`cfb-matchup-${g.event_id}`}>
      <table className="w-full text-[11px] mb-1.5">
        <thead>
          <tr className="text-zinc-500">
            <th className="text-left font-normal" />
            <th className="font-bold text-emerald-300 px-1">{p.abbr} (pick)</th>
            <th className="font-bold text-zinc-400 px-1">{op?.abbr}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, a, b, hi]) => (
            <tr key={k} className="border-t border-zinc-800/60">
              <td className="py-0.5 text-zinc-500">{k}</td>
              <Cell hi={hi === 1}>{a || '—'}</Cell>
              <Cell hi={hi === -1}>{b || '—'}</Cell>
            </tr>
          ))}
        </tbody>
      </table>
      {(f.last3 || []).length > 0 && <p className="text-[10px] text-zinc-500">{p.abbr} last 3: {f.last3.join(' · ')}</p>}
      {(o.last3 || []).length > 0 && <p className="text-[10px] text-zinc-500 mb-1">{op.abbr} last 3: {o.last3.join(' · ')}</p>}
      <p className="text-[11px] font-bold text-white mt-1">Why {p.abbr} wins</p>
      <ul className="space-y-0.5" data-testid={`cfb-why-${g.event_id}`}>
        {whyTheyWin(g).map((w) => (
          <li key={w.t} className={`text-[11px] leading-snug ${w.ok ? 'text-emerald-200/90' : 'text-amber-300/90'}`}>{w.ok ? '✓' : '⚠'} {w.t}</li>
        ))}
      </ul>
    </div>
  );
};
